import {
  getRegion,
  isRegion,
  EU_NAME,
  US_NAME,
  CA_NAME,
  AP_NAME,
  CN_NAME,
  EU_CODE,
  US_CODE,
  CA_CODE,
  AP_CODE,
  CN_CODE,
} from '@storyblok/region-helper'
import type { Region as RegionCode } from '@storyblok/region-helper'
import config from '@/config'
import { t } from '@/utils/helpers/translate'

export const DEFAULT_SPACES = 'defaultSpaces'
export const DEFAULT_REGION = 'eu-central-1'
export const DEFAULT_REGION_KEY = EU_CODE
export const US_REGION = 'us-east-1'

// additional means regions that can be used for spaces in addition to the default region
export const ADDITIONAL_REGIONS: readonly RegionCode[] = [
  US_CODE,
  CA_CODE,
  AP_CODE,
]

// these values are used in the region picker for getting AWS settings
export const CONFIGURABLE_REGIONS = [
  'eu',
  'us-east-1',
  'ca-central-1',
  'ap-southeast-2',
  'cn',
  'local',
]

export type RegionKeys = 'eu' | 'us' | 'ca' | 'ap' | 'cn'
export type RegionServerKeys =
  | 'eu-central-1'
  | 'us-east-1'
  | 'ca-central-1'
  | 'ap-southeast-2'
  | 'cn-north-1'

export type Region = {
  value: RegionCode
  server: RegionServerKeys
  api_v1_path: string
  label: string
  long_label: string
  friendly_label: string
}

export const REGION: Record<RegionCode, Region> = {
  [EU_CODE]: {
    value: EU_CODE,
    server: 'eu-central-1',
    api_v1_path: 'v1',
    label: t('EU'),
    long_label: t('EU Region'),
    friendly_label: EU_NAME,
  },
  [US_CODE]: {
    value: US_CODE,
    server: 'us-east-1',
    api_v1_path: 'v1_us',
    label: t('US'),
    long_label: t('US Region'),
    friendly_label: US_NAME,
  },
  [CA_CODE]: {
    value: CA_CODE,
    server: 'ca-central-1',
    api_v1_path: 'v1_ca',
    label: t('CA'),
    long_label: t('CA Region'),
    friendly_label: CA_NAME,
  },
  [AP_CODE]: {
    value: AP_CODE,
    server: 'ap-southeast-2',
    api_v1_path: 'v1_ap',
    label: t('AU'),
    long_label: t('AU Region'),
    friendly_label: AP_NAME,
  },
  [CN_CODE]: {
    value: CN_CODE,
    server: 'cn-north-1',
    api_v1_path: 'v1',
    label: t('CN'),
    long_label: t('CN Region'),
    friendly_label: CN_NAME,
  },
}

export const getRegionFromSpaceId = (spaceId: number): Region | undefined => {
  const regionCode = getRegion(spaceId)

  // china and eu share the same range but have different backends
  if (
    (regionCode === EU_CODE || regionCode === CN_CODE) &&
    config.isChinaRegion()
  ) {
    return REGION[CN_CODE]
  } else if (regionCode !== undefined) {
    return REGION[regionCode]
  }
}

export const getRegionFromName = (name: string): Region => {
  const firstChars = name?.substring(0, 2)

  if (firstChars && isRegion(firstChars) && REGION[firstChars]) {
    return REGION[firstChars]
  }

  return REGION.eu
}

export type RegionOption = {
  label: string
  value: RegionCode
  server: RegionServerKeys
}

export const ALL_REGIONS = [DEFAULT_REGION_KEY, ...ADDITIONAL_REGIONS] as const

export const serverLocationOptions = (): RegionOption[] => {
  return Object.entries(REGION)
    .filter(([key]) => ALL_REGIONS.includes(key as RegionCode))
    .map(([_, region]) => ({
      label: region.friendly_label,
      value: region.value,
      server: region.server as RegionServerKeys,
    }))
}
